import { useT } from '@/i18n';
import { BAND_STYLE } from '@/styles/tokens';
import type { ResultBand } from '@/api/types';
import { cx } from './cx';

export interface BandPillProps {
  band: ResultBand;
  label?: string;
  className?: string;
}

/** Each band has its own shape (circle, triangle, diamond, dashed ring) so colour is never the only signal. */
export const BandShape = ({ band, size = 11 }: { band: ResultBand; size?: number }) => {
  const c = BAND_STYLE[band].dot;
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 12 12" className="shrink-0" data-shape={band}>
      {band === 'ok' && <circle cx="6" cy="6" r="5.5" fill={c} />}
      {band === 'watch' && <path d="M6 .5 11.5 11H.5z" fill={c} />}
      {band === 'discuss' && <path d="M6 .3 11.7 6 6 11.7.3 6z" fill={c} />}
      {band === 'na' && <circle cx="6" cy="6" r="4.6" fill="none" stroke={c} strokeWidth="1.6" strokeDasharray="2 1.7" />}
    </svg>
  );
};

/** Shape + label. Label comes from `t('band.<band>')` unless overridden. */
export const BandPill = ({ band, label, className = '' }: BandPillProps) => {
  const t = useT();
  const style = BAND_STYLE[band];
  return (
    <span
      data-band={band}
      className={cx('inline-flex items-center gap-2 whitespace-nowrap rounded-pill px-3 py-1 text-label', className)}
      style={{ backgroundColor: style.bg, color: style.fg }}
    >
      <BandShape band={band} />
      {label ?? t(`band.${band}`)}
    </span>
  );
};
